import React, { useState, useEffect } from 'react';
import { Conversation, ConversationService } from '../services/conversationService';

interface ConversationHistoryProps {
  currentConversationId?: string;
  onConversationSelect: (conversation: Conversation) => void;
  onNewConversation: () => void;
}

interface DeleteModalProps {
  isOpen: boolean;
  conversationTitle: string;
  isDeleting: boolean;
  errorMessage: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteModalProps> = ({ isOpen, conversationTitle, isDeleting, errorMessage, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={!isDeleting ? onCancel : undefined} />
      
      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Icon */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          {/* Content */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Conversation
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this conversation?
            </p>
            <p className="text-sm font-medium text-gray-800 bg-gray-100 rounded-lg px-3 py-2 mb-4">
              "{conversationTitle}"
            </p>
            <p className="text-xs text-gray-500 mb-4">
              This action cannot be undone.
            </p>
            {errorMessage && (
              <p className="text-xs text-red-600 mb-2">{errorMessage}</p>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isDeleting ? 'Deleting...' : 'Delete Conversation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ 
  currentConversationId, 
  onConversationSelect,
  onNewConversation 
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false); // Mobile toggle
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; conversationId: string; title: string }>({
    isOpen: false,
    conversationId: '',
    title: '',
  });

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      const userConversations = await ConversationService.getUserConversations(20);
      setConversations(userConversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleConversationClick = (conversation: Conversation) => {
    onConversationSelect(conversation);
    setIsOpen(false); // Close sidebar on mobile
  };

  const handleDeleteConversation = (conversation: Conversation, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent conversation selection
    setDeleteModal({
      isOpen: true,
      conversationId: conversation.id,
      title: conversation.title,
    });
  };

  const confirmDelete = async () => {
    const { conversationId } = deleteModal;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await ConversationService.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      if (conversationId === currentConversationId) {
        onNewConversation();
      }
      setDeleteModal({ isOpen: false, conversationId: '', title: '' });
    } catch {
      setDeleteError('Something went wrong. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, conversationId: '', title: '' });
    setDeleteError(null);
  };

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-20 left-4 z-50 bg-white shadow-md rounded-full p-2 border border-gray-200"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-80 bg-gray-50 border-r border-gray-200 
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-full
      `}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Chat History</h2>
            <button
              onClick={onNewConversation}
              className="px-3 py-1.5 bg-eai-primary-accent text-white rounded-lg text-sm font-medium hover:bg-opacity-90 transition-colors"
            >
              New Chat
            </button>
          </div>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-eai-primary-accent border-t-transparent"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">No conversations yet</p>
              <p className="text-gray-400 text-xs mt-1">Start a new chat to begin</p>
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => handleConversationClick(conversation)}
                className={`
                  group cursor-pointer rounded-lg p-3 transition-colors hover:bg-white hover:shadow-sm border
                  ${currentConversationId === conversation.id 
                    ? 'bg-white shadow-sm border-eai-primary-accent/20 ring-1 ring-eai-primary-accent/20' 
                    : 'border-transparent hover:border-gray-200'
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {conversation.title}
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`
                        inline-flex px-2 py-0.5 rounded text-xs font-medium
                        ${conversation.modelCodename === 'Anastasie'
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                        }
                      `}>
                        {conversation.modelCodename}
                      </span>
                      <span className="text-xs text-gray-500">
                        {conversation.messageCount} messages
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(conversation.lastMessageAt)}
                    </p>
                  </div>
                  
                  <button
                    onClick={(e) => handleDeleteConversation(conversation, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                    title="Delete conversation"
                  >
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <p className="text-xs text-gray-500 text-center">
            {conversations.length > 0 && `${conversations.length} conversations`}
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        conversationTitle={deleteModal.title}
        isDeleting={isDeleting}
        errorMessage={deleteError}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default ConversationHistory;